import { graphql } from "gatsby"
import React, { useMemo, useState } from "react"
import { Col, Container, Form, Row } from "react-bootstrap"
import { BlogLink, PageLayout, PageTitle } from "../components"
import { SEO, Utils, useTranslation } from "../utils"

const POSTS_PER_PAGE = 6
const CATEGORY_IDS = ["all", "weekly", "conference", "tech", "ia", "architecture"]

export default ({ data }) => {
  const { t } = useTranslation()
  const [query, setQuery] = useState("")
  const [selectedCategory, setSelectedCategory] = useState("all")
  const [currentPage, setCurrentPage] = useState(1)

  const allFeaturedImages = data.allFile.edges || []
  const allPosts = data.allMarkdownRemark.edges || []
  const regex = /\/[blog].*\/|$/
  const featuredImageMap = Utils.getImageMap(allFeaturedImages, regex)

  const filteredPosts = useMemo(() => {
    return allPosts.filter(({ node }) => {
      const matchesSearch =
        query === "" ||
        node.frontmatter.title.toLowerCase().includes(query.toLowerCase()) ||
        node.excerpt.toLowerCase().includes(query.toLowerCase())

      const matchesCategory =
        selectedCategory === "all" ||
        node.frontmatter.category === selectedCategory

      return matchesSearch && matchesCategory
    })
  }, [allPosts, query, selectedCategory])

  const totalPages = Math.ceil(filteredPosts.length / POSTS_PER_PAGE)
  const safePage = Math.min(currentPage, totalPages || 1)
  const paginatedPosts = filteredPosts.slice(
    (safePage - 1) * POSTS_PER_PAGE,
    safePage * POSTS_PER_PAGE
  )

  const handleSearch = e => {
    setQuery(e.target.value)
    setCurrentPage(1)
  }

  const handleCategory = cat => {
    setSelectedCategory(cat)
    setCurrentPage(1)
  }

  const handlePageChange = page => {
    setCurrentPage(page)
    window.scrollTo({ top: 0, behavior: "smooth" })
  }

  const getPageNumbers = () => {
    if (totalPages <= 5) return Array.from({ length: totalPages }, (_, i) => i + 1)
    if (currentPage <= 3) return [1, 2, 3, 4, "...", totalPages]
    if (currentPage >= totalPages - 2) return [1, "...", totalPages - 3, totalPages - 2, totalPages - 1, totalPages]
    return [1, "...", currentPage - 1, currentPage, currentPage + 1, "...", totalPages]
  }

  return (
    <PageLayout pageName="blog">
      <SEO title={t("blog.title")} />
      <PageTitle title={t("blog.title")} />

      <Container className="py-4">

        {/* Barre de recherche */}
        <Row className="justify-content-center mb-3">
          <Col md={6}>
            <Form.Control
              type="text"
              placeholder={t("blog.search_placeholder")}
              value={query}
              onChange={handleSearch}
              className="blog-search-input"
            />
          </Col>
        </Row>

        {/* Filtres catégories */}
        <div className="d-flex flex-wrap justify-content-center mb-4 blog-category-filters">
          {CATEGORY_IDS.map(id => (
            <button
              key={id}
              onClick={() => handleCategory(id)}
              className={`blog-category-btn ${selectedCategory === id ? "active" : ""}`}
            >
              {t(`blog.categories.${id}`)}
            </button>
          ))}
        </div>

        {/* Compteur */}
        <div className="d-flex justify-content-between align-items-center mb-4">
          <small className="text-muted">
            {filteredPosts.length === allPosts.length
              ? <span><strong>{filteredPosts.length}</strong> {t("blog.count_all")}</span>
              : <span><strong>{filteredPosts.length}</strong> {t("blog.count_filtered")} <strong>{allPosts.length}</strong></span>
            }
          </small>
          {totalPages > 1 && (
            <small className="text-muted">{t("blog.page_of")} {safePage} / {totalPages}</small>
          )}
        </div>

        {/* Articles */}
        {paginatedPosts.length > 0 ? (
          paginatedPosts.map(({ node }) => (
            <div key={node.id} className="mb-4">
              <BlogLink
                date={node.frontmatter.date}
                to={node.fields.slug}
                featuredImage={featuredImageMap[node.fields.slug]}
                title={node.frontmatter.title}
                excerpt={node.excerpt}
                tags={node.frontmatter.tags}
                time={node.frontmatter.read_time}
              />
            </div>
          ))
        ) : (
          <div className="text-center py-5">
            <p className="text-muted">{t("blog.no_results")}</p>
            <button
              className="blog-category-btn active"
              onClick={() => { setQuery(""); setSelectedCategory("all"); setCurrentPage(1) }}
            >
              {t("blog.reset")}
            </button>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <nav className="mt-5" aria-label="Pagination">
            <ul className="pagination justify-content-center flex-wrap">
              <li className={`page-item ${safePage === 1 ? "disabled" : ""}`}>
                <button className="page-link" onClick={() => handlePageChange(safePage - 1)}>
                  {t("blog.prev")}
                </button>
              </li>
              {getPageNumbers().map((page, i) =>
                page === "..." ? (
                  <li key={`e-${i}`} className="page-item disabled">
                    <span className="page-link">…</span>
                  </li>
                ) : (
                  <li key={page} className={`page-item ${safePage === page ? "active" : ""}`}>
                    <button className="page-link" onClick={() => handlePageChange(page)}>
                      {page}
                    </button>
                  </li>
                )
              )}
              <li className={`page-item ${safePage === totalPages ? "disabled" : ""}`}>
                <button className="page-link" onClick={() => handlePageChange(safePage + 1)}>
                  {t("blog.next")}
                </button>
              </li>
            </ul>
          </nav>
        )}
      </Container>
    </PageLayout>
  )
}

export const query = graphql`
  query {
    allMarkdownRemark(
      filter: { fileAbsolutePath: { regex: "/blog/" } }
      sort: { fields: [frontmatter___date], order: DESC }
    ) {
      totalCount
      edges {
        node {
          id
          frontmatter {
            title
            tags
            category
            date(formatString: "DD MMMM, YYYY")
            read_time
          }
          fields {
            slug
          }
          excerpt
        }
      }
    }
    allFile(
      filter: {
        extension: { eq: "jpg" }
        relativePath: { regex: "/feature/" }
        relativeDirectory: { regex: "/content/blog/" }
      }
    ) {
      edges {
        node {
          childImageSharp {
            fluid(maxWidth: 400) {
              ...GatsbyImageSharpFluid
            }
          }
          relativePath
        }
      }
    }
  }
`
